import React from "react";
import classNames from "classnames";
import type { SectionHeaderProps, SectionHeaderTag } from "./types";
import styles from "./SectionHeader.module.css";

/**
 * SectionHeader atom - A consistent header for sections and subsections.
 *
 * Provides semantic HTML heading levels with consistent styling.
 * Supports optional icons and action elements.
 *
 * @example
 * ```tsx
 * // Simple header
 * <SectionHeader as="h3" size="md">
 *   Organization Settings
 * </SectionHeader>
 *
 * // With icon
 * <SectionHeader as="h3" icon={<Icon name="settings" />}>
 *   Settings
 * </SectionHeader>
 *
 * // With action
 * <SectionHeader as="h2" action={<Button size="sm">Add</Button>}>
 *   Users
 * </SectionHeader>
 * ```
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  children,
  as: Tag = "h3",
  size = "md",
  icon,
  action,
  className,
  "data-testid": testId,
  ...props
}) => {
  const Component = Tag as SectionHeaderTag;

  return (
    <Component
      className={classNames(styles.header, styles[size], className)}
      data-testid={testId}
      {...props}
    >
      <span className={styles.content}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <span className={styles.text}>{children}</span>
      </span>
      {action && <span className={styles.action}>{action}</span>}
    </Component>
  );
};

SectionHeader.displayName = "SectionHeader";

export default SectionHeader;
